import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useLanguage } from '../../lib/LanguageContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { LifeBuoy, Plus, MessageSquare, Loader2, Send, RefreshCw, AlertCircle } from 'lucide-react';

const STATUS_BG = {
  open: 'bg-blue-100 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800',
  in_progress: 'bg-amber-100 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800',
  resolved: 'bg-emerald-100 text-emerald-700 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800',
  closed: 'bg-slate-200 text-slate-700 border-slate-300 dark:bg-slate-700 dark:text-slate-200 dark:border-slate-600',
};
const PRIORITY_BG = {
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  normal: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  high: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  urgent: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
};

function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleString(); } catch (e) { return '—'; }
}

export default function ClientTickets() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ subject: '', message: '', priority: 'normal' });
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  const statusLabel = (s) => t(`tickets.st.${s}`) || s;
  const priorityLabel = (p) => t(`tickets.pr.${p}`) || p;

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get('/client/tickets');
      setItems(Array.isArray(r.data) ? r.data : (r.data?.tickets || []));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('tickets.loadError'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const createTicket = async () => {
    if (!form.subject.trim() || !form.message.trim()) {
      toast.error(t('tickets.subjectMsgRequired'));
      return;
    }
    try {
      setCreating(true);
      await api.post('/client/tickets', {
        subject: form.subject.trim(),
        message: form.message.trim(),
        priority: form.priority,
      });
      toast.success(t('tickets.ticketCreated'));
      setShowNew(false);
      setForm({ subject: '', message: '', priority: 'normal' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('tickets.createError'));
    } finally {
      setCreating(false);
    }
  };

  const openDetail = async (id) => {
    try {
      setDetailLoading(true);
      const r = await api.get(`/client/tickets/${id}`).catch(async () => {
        const found = items.find((ti) => ti.id === id);
        return { data: found };
      });
      setSelected(r.data);
      setReply('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('tickets.loadError'));
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!selected?.id) return;
    try {
      const r = await api.get(`/client/tickets/${selected.id}`);
      setSelected(r.data);
    } catch (e) { /* ignore */ }
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected?.id) return;
    try {
      setSending(true);
      await api.post(`/client/tickets/${selected.id}/reply`, { message: reply.trim() });
      toast.success(t('tickets.messageSent'));
      setReply('');
      await refreshDetail();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t('tickets.sendError'));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold dark:text-white flex items-center gap-2">
            <LifeBuoy className="w-7 h-7 text-blue-700 dark:text-blue-400" /> {t('tickets.mySupport')}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('tickets.subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} data-testid="btn-client-tickets-refresh">
            <RefreshCw className="w-4 h-4 mr-1" /> {t('tickets.refresh')}
          </Button>
          <Button className="bg-blue-700 hover:bg-blue-800" onClick={() => setShowNew(true)} data-testid="btn-client-tickets-new">
            <Plus className="w-4 h-4 mr-1" /> {t('tickets.newTicket')}
          </Button>
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" /> {t('tickets.loading')}
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500 dark:text-slate-400 text-sm">
            <AlertCircle className="w-6 h-6 mx-auto mb-2" />
            {t('tickets.noneYet')}
          </div>
        ) : (
          <div className="divide-y dark:divide-slate-800">
            {items.map((ti) => (
              <button
                key={ti.id}
                onClick={() => openDetail(ti.id)}
                className="w-full text-left p-4 hover:bg-slate-50 dark:hover:bg-slate-800/40 transition flex flex-wrap items-center gap-4"
                data-testid={`row-client-ticket-${ti.id}`}
              >
                <div className="flex-1 min-w-[180px]">
                  <div className="font-semibold dark:text-white">{ti.subject}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 line-clamp-1">{ti.message}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-md font-medium border ${STATUS_BG[ti.status] || ''}`}>
                    {statusLabel(ti.status)}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-md font-medium ${PRIORITY_BG[ti.priority] || ''}`}>
                    {priorityLabel(ti.priority)}
                  </span>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                  <MessageSquare className="w-3.5 h-3.5" /> {ti.response_count ?? (ti.responses?.length || 0)}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 min-w-[140px] text-right">
                  {fmtDate(ti.updated_at || ti.created_at)}
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* New ticket dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="dark:text-white">{t('tickets.newTicket')}</DialogTitle>
            <DialogDescription>
              {t('tickets.newDesc')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>{t('tickets.subjectLabel')}</Label>
              <Input
                value={form.subject}
                onChange={(e) => setForm({ ...form, subject: e.target.value })}
                placeholder={t('tickets.subjectPh')}
                data-testid="input-new-ticket-subject"
              />
            </div>
            <div>
              <Label>{t('tickets.priorityLabel')}</Label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="select-new-ticket-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">{t('tickets.pr.low')}</SelectItem>
                  <SelectItem value="normal">{t('tickets.pr.normal')}</SelectItem>
                  <SelectItem value="high">{t('tickets.pr.high')}</SelectItem>
                  <SelectItem value="urgent">{t('tickets.pr.urgent')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>{t('tickets.messageLabel')}</Label>
              <Textarea
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder={t('tickets.messagePh')}
                data-testid="textarea-new-ticket-message"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} disabled={creating}>{t('tickets.cancel')}</Button>
            <Button
              onClick={createTicket}
              disabled={creating || !form.subject.trim() || !form.message.trim()}
              className="bg-blue-700 hover:bg-blue-800"
              data-testid="btn-new-ticket-submit"
            >
              {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
              {t('tickets.send')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-white">
              <LifeBuoy className="w-5 h-5 text-blue-700 dark:text-blue-400" />
              {selected?.subject}
            </DialogTitle>
            <DialogDescription>
              {selected && (
                <span className="inline-flex items-center gap-2">
                  <span className={`text-xs px-2 py-1 rounded-md font-medium border ${STATUS_BG[selected.status] || ''}`}>
                    {statusLabel(selected.status)}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded-md font-medium ${PRIORITY_BG[selected.priority] || ''}`}>
                    {priorityLabel(selected.priority)}
                  </span>
                  <span className="text-xs text-slate-500 dark:text-slate-400">{t('tickets.createdAt')} {fmtDate(selected.created_at)}</span>
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          {detailLoading || !selected ? (
            <div className="py-8 text-center text-slate-500 dark:text-slate-400">
              <Loader2 className="w-5 h-5 mx-auto animate-spin" />
            </div>
          ) : (
            <div className="space-y-3">
              <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
                <div className="flex justify-end">
                  <div className="max-w-[80%] bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 text-sm">
                    <div className="text-xs font-semibold text-blue-100 mb-1">{t('tickets.you')} · {fmtDate(selected.created_at)}</div>
                    <div className="whitespace-pre-line">{selected.message}</div>
                  </div>
                </div>
                {(selected.responses || []).map((r, idx) => {
                  const isAdmin = r.author_role === 'admin' || r.is_admin;
                  return (
                    <div key={r.id || idx} className={`flex ${isAdmin ? '' : 'justify-end'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm ${
                        isAdmin
                          ? 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 rounded-tl-sm'
                          : 'bg-blue-600 text-white rounded-tr-sm'
                      }`}>
                        <div className={`text-xs font-semibold mb-1 ${isAdmin ? 'text-slate-500 dark:text-slate-400' : 'text-blue-100'}`}>
                          {isAdmin ? (r.author_name || t('tickets.supportTeam')) : t('tickets.you')} · {fmtDate(r.created_at)}
                        </div>
                        <div className="whitespace-pre-line">{r.message}</div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {selected.status !== 'closed' && (
                <div className="space-y-2 pt-2 border-t dark:border-slate-700">
                  <Label className="dark:text-slate-200">{t('tickets.yourReply')}</Label>
                  <Textarea
                    rows={3}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    placeholder={t('tickets.replyPh')}
                    data-testid="textarea-client-ticket-reply"
                  />
                  <div className="flex justify-end">
                    <Button
                      onClick={sendReply}
                      disabled={sending || !reply.trim()}
                      className="bg-blue-700 hover:bg-blue-800"
                      data-testid="btn-client-ticket-send"
                    >
                      {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                      {t('tickets.send')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
